import { cn } from 'src/helpers/style'

import {
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'

export interface IArticleListItem {
  id: string
  title: string
  meta: string
}

export type ArticleListLayout = 'stacked' | 'inline-meta'

export interface IArticleListProps {
  items: IArticleListItem[]
  ariaLabel?: string
  onItemClick?: (item: IArticleListItem) => void
  layout?: ArticleListLayout
}

const rowBaseClassName = 'flex w-full text-left transition-colors'

const stackedRowClassName = 'flex-col items-stretch gap-2 p-3'

// 68px row height matches the Figma "More recent news" mobile spec (node 2737:9831).
const inlineRowClassName =
  'min-h-[68px] items-center justify-between gap-3 px-3 py-2'

const interactiveRowClassName = cn(
  'cursor-pointer border-0 bg-transparent',
  'hover:bg-state-hover-surface',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page',
)

const StackedRowContent = ({
  item,
}: {
  item: IArticleListItem
}): JSX.Element => (
  <>
    <Typography
      variant={TypographyVariant.title2Xs}
      weight={TypographyWeight.light}
      className="text-content-primary"
    >
      {item.title}
    </Typography>
    <Typography
      variant={TypographyVariant.bodyXxs}
      className="text-content-secondary"
    >
      {item.meta}
    </Typography>
  </>
)

const InlineRowContent = ({
  item,
}: {
  item: IArticleListItem
}): JSX.Element => (
  <>
    <Typography
      variant={TypographyVariant.title2Xs}
      weight={TypographyWeight.light}
      className="min-w-0 flex-1 text-content-primary"
    >
      {item.title}
    </Typography>
    <Typography
      variant={TypographyVariant.bodyXs}
      className="shrink-0 whitespace-nowrap text-right text-content-secondary"
    >
      {item.meta}
    </Typography>
  </>
)

const RowContent = ({
  item,
  layout,
}: {
  item: IArticleListItem
  layout: ArticleListLayout
}): JSX.Element =>
  layout === 'inline-meta' ? (
    <InlineRowContent item={item} />
  ) : (
    <StackedRowContent item={item} />
  )

export const ArticleList = ({
  items,
  ariaLabel,
  onItemClick,
  layout = 'stacked',
}: IArticleListProps): JSX.Element => {
  const layoutClassName =
    layout === 'inline-meta' ? inlineRowClassName : stackedRowClassName

  return (
    <ul
      aria-label={ariaLabel}
      className="m-0 w-full list-none divide-y divide-border-default border-y border-border-default p-0"
    >
      {items.map((item): JSX.Element => {
        if (onItemClick) {
          return (
            <li key={item.id}>
              <button
                type="button"
                className={cn(
                  rowBaseClassName,
                  layoutClassName,
                  interactiveRowClassName,
                )}
                onClick={(): void => onItemClick(item)}
              >
                <RowContent item={item} layout={layout} />
              </button>
            </li>
          )
        }

        return (
          <li key={item.id} className={cn(rowBaseClassName, layoutClassName)}>
            <RowContent item={item} layout={layout} />
          </li>
        )
      })}
    </ul>
  )
}
